import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronRight, Lock, CreditCard, ArrowLeft, Check } from "lucide-react";
import { http, formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { toast } from "sonner";

const STEPS = ["Contact & Shipping", "Delivery Method", "Payment"];

function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">{label}</label>
      {children}
      {error && <div className="text-xs text-red-500 mt-1">{error}</div>}
    </div>
  );
}

function Input(props) {
  return <input {...props} className={`w-full px-4 py-3 rounded-xl border text-sm outline-none transition-colors ${props.error ? "border-red-300 focus:border-red-400" : "border-gray-200 focus:border-yellow-400"} ${props.className || ""}`} />;
}

export default function Checkout() {
  const { user } = useAuth();
  const { items, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const stripeRef = useRef(null);
  const cardElementRef = useRef(null);
  const [step, setStep] = useState(0);
  const [shippingZones, setShippingZones] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState({ stripe: false, paypal: false });
  const [stripeKey, setStripeKey] = useState("");
  const [paypalClientId, setPaypalClientId] = useState("");
  const [stripeReady, setStripeReady] = useState(false);
  const [paypalReady, setPaypalReady] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [placing, setPlacing] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    email: user?.email || "",
    name: user?.name || "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  });

  useEffect(() => {
    http.get("/store/settings").then(r => {
      const s = r.data;
      setPaymentMethods({ stripe: !!s.stripe_enabled, paypal: !!s.paypal_enabled });
      setStripeKey(s.stripe_publishable_key || "");
      setPaypalClientId(s.paypal_client_id || "");
      if (!s.stripe_enabled && s.paypal_enabled) setPaymentMethod("paypal");
    }).catch(() => {});
    http.get("/store/shipping-zones").then(r => {
      setShippingZones(r.data);
      if (r.data.length > 0) setSelectedZone(r.data[0]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!stripeKey || step !== 2 || paymentMethod !== "stripe") return;
    const s = document.createElement("script");
    s.src = "https://js.stripe.com/v3/";
    s.onload = () => {
      stripeRef.current = window.Stripe(stripeKey);
      const elements = stripeRef.current.elements();
      const card = elements.create("card", { style: { base: { fontSize: "16px", color: "#1a1a1a", fontFamily: "Manrope, sans-serif" } } });
      card.mount("#stripe-card-element");
      cardElementRef.current = card;
      setStripeReady(true);
    };
    document.head.appendChild(s);
    return () => { document.head.contains(s) && document.head.removeChild(s); };
  }, [stripeKey, step, paymentMethod]);

  useEffect(() => {
    if (!paypalClientId || step !== 2 || paymentMethod !== "paypal") return;
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${paypalClientId}&currency=USD`;
    s.onload = () => {
      setPaypalReady(true);
      window.paypal.Buttons({
        createOrder: async () => {
          const r = await http.post("/store/payment/paypal/create", buildOrderPayload());
          return r.data.paypal_order_id;
        },
        onApprove: async (data) => {
          try {
            const r = await http.post(`/store/payment/paypal/capture/${data.orderID}`, buildOrderPayload());
            clearCart();
            navigate(`/store/order/${r.data.order_id}`, { replace: true });
          } catch (e) { toast.error(formatErr(e)); }
        },
        onError: () => toast.error("PayPal payment failed. Please try again."),
      }).render("#paypal-button-container");
    };
    document.head.appendChild(s);
  }, [paypalClientId, step, paymentMethod]);

  if (items.length === 0) {
    navigate("/store/cart", { replace: true });
    return null;
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.email) e.email = "Required";
    if (!form.name) e.name = "Required";
    if (!form.address_line1) e.address_line1 = "Required";
    if (!form.city) e.city = "Required";
    if (!form.country) e.country = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildOrderPayload = () => ({
    items: items.map(i => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty, price: i.price, name: i.product_name })),
    shipping_address: { name: form.name, email: form.email, phone: form.phone, line1: form.address_line1, line2: form.address_line2, city: form.city, state: form.state, zip: form.zip, country: form.country },
    shipping_zone_id: selectedZone?.id || null,
    subtotal,
    shipping_cost: selectedZone?.price || 0,
    total: subtotal + (selectedZone?.price || 0),
  });

  const handleStripeSubmit = async () => {
    if (!stripeReady || !cardElementRef.current) { toast.error("Payment form not ready"); return; }
    setPlacing(true);
    try {
      const { data: intentData } = await http.post("/store/payment/stripe/intent", buildOrderPayload());
      const { error, paymentIntent } = await stripeRef.current.confirmCardPayment(intentData.client_secret, {
        payment_method: { card: cardElementRef.current, billing_details: { name: form.name, email: form.email } },
      });
      if (error) throw new Error(error.message);
      if (paymentIntent.status === "succeeded") {
        const r = await http.post("/store/checkout", { ...buildOrderPayload(), payment_intent_id: paymentIntent.id, payment_method: "stripe" });
        clearCart();
        navigate(`/store/order/${r.data.order_id}`, { replace: true });
      }
    } catch (e) { toast.error(formatErr(e) || e.message); }
    finally { setPlacing(false); }
  };

  const shippingCost = selectedZone?.price || 0;
  const orderTotal = subtotal + shippingCost;

  return (
    <div className="max-w-5xl mx-auto grid lg:grid-cols-5 gap-8">
      {/* Left: Form */}
      <div className="lg:col-span-3 space-y-6">
        {/* Step indicator */}
        <nav className="flex items-center gap-2 text-sm">
          <Link to="/store/cart" className="flex items-center gap-1 text-gray-500 hover:text-yellow-600"><ArrowLeft size={14} /> Cart</Link>
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <ChevronRight size={13} className="text-gray-300" />
              <span className={`font-semibold ${i === step ? "text-yellow-600" : i < step ? "text-green-600" : "text-gray-400"}`}>
                {i < step && <Check size={13} className="inline mr-1" />}{s}
              </span>
            </React.Fragment>
          ))}
        </nav>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
          {step === 0 && (
            <>
              <h2 className="font-bold text-gray-900 text-lg">Contact & Shipping</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name" error={errors.name}><Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="John Doe" error={errors.name} /></Field>
                <Field label="Email" error={errors.email}><Input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="john@example.com" error={errors.email} /></Field>
                <Field label="Phone" ><Input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+1 555 000 0000" /></Field>
                <Field label="Country" error={errors.country}><Input value={form.country} onChange={e => set("country", e.target.value)} placeholder="Nigeria" error={errors.country} /></Field>
              </div>
              <Field label="Address Line 1" error={errors.address_line1}><Input value={form.address_line1} onChange={e => set("address_line1", e.target.value)} placeholder="123 Main Street" error={errors.address_line1} /></Field>
              <Field label="Address Line 2"><Input value={form.address_line2} onChange={e => set("address_line2", e.target.value)} placeholder="Apartment, suite, etc." /></Field>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="City" error={errors.city}><Input value={form.city} onChange={e => set("city", e.target.value)} placeholder="Lagos" error={errors.city} /></Field>
                <Field label="State / Province"><Input value={form.state} onChange={e => set("state", e.target.value)} placeholder="Lagos State" /></Field>
                <Field label="ZIP / Postal Code"><Input value={form.zip} onChange={e => set("zip", e.target.value)} placeholder="100001" /></Field>
              </div>
              <button onClick={() => { if (validate()) setStep(1); }} className="w-full py-4 rounded-2xl font-bold text-white flex items-center justify-center gap-2" style={{ background: "#0F1E38" }}>
                Continue to Delivery <ChevronRight size={16} />
              </button>
            </>
          )}

          {step === 1 && (
            <>
              <h2 className="font-bold text-gray-900 text-lg">Delivery Method</h2>
              {shippingZones.length === 0
                ? <div className="text-sm text-gray-500 py-4 text-center">No shipping options configured. Contact the store.</div>
                : <div className="space-y-2">
                  {shippingZones.map(z => (
                    <label key={z.id} className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${selectedZone?.id === z.id ? "border-yellow-400 bg-yellow-50" : "border-gray-200 hover:border-gray-300"}`}>
                      <input type="radio" name="zone" checked={selectedZone?.id === z.id} onChange={() => setSelectedZone(z)} className="accent-yellow-500" />
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">{z.name}</div>
                        {z.description && <div className="text-xs text-gray-500 mt-0.5">{z.description}</div>}
                        {z.estimated_days && <div className="text-xs text-gray-400 mt-0.5">Estimated: {z.estimated_days}</div>}
                      </div>
                      <span className="font-bold text-gray-900">{z.price === 0 ? "Free" : `$${Number(z.price).toFixed(2)}`}</span>
                    </label>
                  ))}
                </div>
              }
              <div className="flex gap-3">
                <button onClick={() => setStep(0)} className="flex-1 py-3 rounded-2xl font-bold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors">Back</button>
                <button onClick={() => setStep(2)} className="flex-1 py-3 rounded-2xl font-bold text-white" style={{ background: "#0F1E38" }}>Continue to Payment</button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="font-bold text-gray-900 text-lg flex items-center gap-2"><Lock size={16} className="text-green-500" /> Payment</h2>
              {paymentMethods.stripe && paymentMethods.paypal && (
                <div className="flex gap-2">
                  {[{ k: "stripe", l: "Card (Stripe)" }, { k: "paypal", l: "PayPal" }].map(({ k, l }) => (
                    <button key={k} onClick={() => setPaymentMethod(k)} className={`flex-1 py-2.5 rounded-xl font-semibold text-sm border-2 transition-colors ${paymentMethod === k ? "border-yellow-400 text-yellow-700 bg-yellow-50" : "border-gray-200 text-gray-600"}`}>{l}</button>
                  ))}
                </div>
              )}
              {paymentMethod === "stripe" && (
                <div>
                  <div id="stripe-card-element" className="p-4 rounded-xl border border-gray-200 bg-gray-50 min-h-[50px]" />
                  {!stripeKey && <div className="text-xs text-orange-500 mt-2">Stripe not configured. Contact admin.</div>}
                </div>
              )}
              {paymentMethod === "paypal" && (
                <div id="paypal-button-container" className="min-h-[50px]">
                  {!paypalReady && <div className="text-sm text-gray-500 text-center py-4">Loading PayPal…</div>}
                  {!paypalClientId && <div className="text-xs text-orange-500">PayPal not configured. Contact admin.</div>}
                </div>
              )}
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="flex-1 py-3 rounded-2xl font-bold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors">Back</button>
                {paymentMethod === "stripe" && (
                  <button onClick={handleStripeSubmit} disabled={placing || !stripeReady} className="flex-1 py-3 rounded-2xl font-bold text-white disabled:opacity-50" style={{ background: "#0F1E38" }}>
                    {placing ? "Processing…" : `Pay $${orderTotal.toFixed(2)}`}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right: Order summary */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sticky top-24 space-y-4">
          <h2 className="font-bold text-gray-900">Order Summary</h2>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {items.map(item => (
              <div key={item.key} className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-50 shrink-0 relative">
                  {item.product_image && <img src={item.product_image} alt="" className="w-full h-full object-cover" />}
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs font-bold text-white grid place-items-center" style={{ background: "#0F1E38" }}>{item.qty}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">{item.product_name}</div>
                  {item.variant_label && <div className="text-xs text-gray-500">{item.variant_label}</div>}
                </div>
                <div className="text-sm font-bold text-gray-900">${(item.price * item.qty).toFixed(2)}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-100 pt-3 space-y-1.5 text-sm">
            <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-gray-600"><span>Shipping</span><span>{shippingCost === 0 ? "Free" : `$${shippingCost.toFixed(2)}`}</span></div>
            <div className="flex justify-between font-bold text-gray-900 text-base pt-1 border-t border-gray-100"><span>Total</span><span>${orderTotal.toFixed(2)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
